/**
 * `_LIMSRound` — the custom worksheet function OG injects into SDMS template
 * formulas, implemented for Univer's formula engine.
 *
 * WHY THIS EXISTS. Legacy "Apply Rounding" rewrites a result cell as
 * `=_LIMSRound(<original expression>, sigFig, cutOff, decimals, rptLimit,
 * defaultResult)` (SpreadsheetEntryController.RoundCellInPlace), so every SAVED
 * SDMS sheet carries these calls — 22 of them in template 9119 alone, including
 * the Rec % / RPD % cells. Univer has no such function, so those cells evaluated
 * to an error and every formula depending on them went blank. That is the
 * "formula calculation is not working" report, and the previously-undiagnosed
 * "Rec % / RPD % / QCStatus blank on Cal/CCV rows".
 *
 * The rounding rule is a port of legacy `RoundingHelper.ApplyRounding` — the SAME
 * three ordered rules as the server-side `LimsRounding.Apply`
 * (Application/Common/Rounding/LimsRounding.cs); keep the two in step:
 *   1. value < RptLimit          → DefaultResult   (tested on the ORIGINAL value)
 *   2. RptLimit ≤ value < CutOff → Decimal
 *   3. value ≥ CutOff (or none)  → SigFig, else Decimal, else the value unchanged
 *
 * Arguments arrive as Excel strings, e.g.
 *   _LIMSRound(<expr>,"3","100","1","","")     and
 *   _LIMSRound(<expr>,"3","","","0.5","BRL")
 * so "" means "not configured" — as do placeholders like "N/A" / "----".
 *
 * NON-NUMERIC INPUT PASSES THROUGH. The wrapped expression frequently evaluates
 * to "" or to a string such as "<0.5" (the inner IF already substituted a
 * below-limit marker); returning it untouched is what legacy does and keeps those
 * cells looking identical to OG.
 */

import { BaseFunction, BooleanValueObject, ErrorType, ErrorValueObject, FUNCTION_NAMES_MATH,
         IFunctionService, NullValueObject, NumberValueObject, StringValueObject } from '@univerjs/engine-formula';
import type { BaseValueObject } from '@univerjs/engine-formula';
import { IUniverInstanceService } from '@univerjs/core';
import { formatSF } from './sgfntFormat';

/** Parse a CONFIG argument (sigFig/cutOff/decimals/rptLimit). Blank, placeholder,
 *  or non-numeric ⇒ not configured. A leading comparison operator is tolerated
 *  because limits are sometimes configured as "<0.5". */
function num(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const s = String(v).trim().replace(/^[<>=]+/, '');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Parse the VALUE being rounded — STRICTLY, with no operator stripping.
 *  A cell reading "<0.5" is a below-limit MARKER the inner formula already
 *  produced (`"<" & I12` in template 9119); treating it as the number 0.5 and
 *  rounding it would silently turn a censored result into a reported one. So
 *  anything not purely numeric passes through untouched. */
function valueNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (s === '' || !/^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function int(v: unknown): number | null {
  const n = num(v);
  return n === null ? null : Math.trunc(n);
}

/** Round to the nearest integer, ties to EVEN (banker's rounding) — matches OG
 *  `E_RoundOff` (VB `Math.Round`, round-half-to-even). A tie is detected within a
 *  small epsilon because the scaled value is a binary float (VB does it on Decimal,
 *  which is exact); away from a tie, plain rounding is used. (2026-09-07, aligned to
 *  E:\...\Roundoff\E_RoundOff\Roundoff.vb) */
function roundHalfToEven(x: number): number {
  if (!Number.isFinite(x)) return x;
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x);
  const floor = Math.floor(ax);
  const diff = ax - floor;
  const EPS = 1e-9;
  let r: number;
  if (Math.abs(diff - 0.5) < EPS) r = (floor % 2 === 0) ? floor : floor + 1;   // tie -> even
  else r = Math.round(ax);                                                     // non-tie
  return sign * r;
}

/** Fixed decimals, ROUND-HALF-AWAY-FROM-ZERO — matches OG E_RoundOff `FormatDecimalValue`
 *  (validated byte-identical to the net461 DLL: 2.345→2.35, 0.125→0.13, 1.005→1.01,
 *  -2.345→-2.35). This is DISTINCT from `toSigFig`, which is banker's (also matching OG — the
 *  decimal path and the sig-fig path round differently in E_RoundOff). The scaled magnitude is
 *  de-noised with `toPrecision(15)` — the same trick `toSigFig` uses — so a binary-float
 *  underestimate (1.005*100 = 100.4999…, 2.345*100 = 234.4999…) rounds on the exact decimal the
 *  way OG's Decimal (`CDec`) arithmetic does, not the raw float.
 *  (2026-09-12: reverted the 2026-09-07 "aligned to banker's" change — the SDMS formula-engine
 *  parity review + the committed E_RoundOff goldens confirm the decimal path is half-away; this
 *  also matches the server-side E_RoundOff, removing a client/server inconsistency.) */
function toDecimals(value: number, decimals: number): number {
  const d = Math.min(Math.max(decimals, 0), 20);
  if (!Number.isFinite(value)) return value;
  const f = 10 ** d;
  const sign = value < 0 ? -1 : 1;
  // Math.round is half-up; on the (positive) de-noised magnitude that is half-away, and the
  // sign is re-applied so negatives round away from zero too (−2.345 → −2.35).
  const deNoised = Number((Math.abs(value) * f).toPrecision(15));
  return Number(((sign * Math.round(deNoised)) / f).toFixed(d));
}

/** Round to N significant figures — OG E_RoundOff `RoundSignificantDbl`: scale by
 *  10^(floor(log10|v|)+1-sig), round-half-to-even, scale back. `Digits<=0` returns the
 *  value unchanged (OG guard). Uses `log(x)/log(10)` (not Math.log10) to mirror OG's
 *  own `Log10` so the exponent lands on the same boundary as OG. */
function toSigFig(value: number, sig: number): number {
  if (value === 0 || sig <= 0) return value;
  const exponent = Math.floor(Math.log(Math.abs(value)) / Math.LN10) + 1 - sig;
  const scaling = 10 ** exponent;
  if (!Number.isFinite(scaling) || scaling === 0) return value;
  // toPrecision(15) strips binary-float noise (86.3*1 -> 86.30000000000001) the way OG's
  // Decimal arithmetic never introduces it; 15 sig digits is well within double precision.
  return Number((roundHalfToEven(value / scaling) * scaling).toPrecision(15));
}

/** The rule itself — exported for unit tests. */
export function limsRound(
  value: unknown,
  sigFig: unknown,
  cutOff: unknown,
  decimals: unknown,
  rptLimit: unknown,
  defaultResult: unknown,
): string | number {
  // Univer hands a blank cell through as null/'' — keep the cell blank, don't 0-fill.
  if (value === null || value === undefined || String(value).trim() === '') return '';

  const numeric = valueNum(value);
  if (numeric === null) return String(value);   // "<0.5", "ND", … pass through verbatim

  const rpt = num(rptLimit);
  const cut = num(cutOff);
  const sig = int(sigFig);
  const dec = int(decimals);

  // Rule 1 — below the reporting limit the numeric result is not reported.
  // Deliberately tested on the ORIGINAL value: 0.0496 rounds to 0.0, which would
  // look below-limit even though the input was not.
  if (rpt !== null && numeric < rpt) {
    return defaultResult === null || defaultResult === undefined ? '' : String(defaultResult);
  }
  // Rule 2 — between the reporting limit and the cut-off, round by decimals.
  if (cut !== null && numeric < cut) return dec !== null ? toDecimals(numeric, dec) : numeric;
  // Rule 3 — at/above the cut-off (or none configured), significant figures.
  if (sig !== null && sig > 0) return toSigFig(numeric, sig);
  if (dec !== null) return toDecimals(numeric, dec);
  return numeric;
}

/** The rounded value as a DISPLAY STRING with its intended trailing zeros — what OG's
 *  RoundCellInPlace feeds to BuildNumberFormat to pick the cell's number format (e.g. the
 *  decimal rule at dec=1 gives "91.0", a 3-sig-fig result gives "0.00456"). Mirrors limsRound's
 *  SAME rule cascade (keep the two in sync); returns the raw string for a non-numeric/default
 *  result. Consumed by the SDMS Apply Rounding per-value number format. */
export function limsRoundText(
  value: unknown, sigFig: unknown, cutOff: unknown,
  decimals: unknown, rptLimit: unknown, defaultResult: unknown,
): string {
  if (value === null || value === undefined || String(value).trim() === '') return '';
  const numeric = valueNum(value);
  if (numeric === null) return String(value);
  const rpt = num(rptLimit);
  const cut = num(cutOff);
  const sig = int(sigFig);
  const dec = int(decimals);
  const fix = (val: number, d: number) => toDecimals(val, d).toFixed(Math.min(Math.max(d, 0), 20));
  if (rpt !== null && numeric < rpt) return defaultResult === null || defaultResult === undefined ? '' : String(defaultResult);
  if (cut !== null && numeric < cut) return dec !== null ? fix(numeric, dec) : String(numeric);
  if (sig !== null && sig > 0) {
    const r = toSigFig(numeric, sig);
    const e = Math.floor(Math.log(Math.abs(r || numeric)) / Math.LN10);
    const d = Math.max(0, sig - 1 - e);
    return d > 0 ? r.toFixed(Math.min(d, 20)) : String(r);
  }
  if (dec !== null) return fix(numeric, dec);
  return String(numeric);
}

/** Univer passes each argument as a value object; unwrap to a primitive.
 *
 *  ARRAY FIRST -- this order is the whole fix (client 2026-08-18, "Rec % shows 0
 *  where LIMS shows 104"). When _LIMSRound's first argument is an EXPRESSION
 *  spanning references -- as it is on every real template, e.g.
 *  _LIMSRound(IF(OR(ISBLANK(B8),..),"",..G8/L8*100),"3","100","1","","") -- Univer
 *  resolves it to a 1x1 ArrayValueObject, and ArrayValueObject.getValue() returns
 *  **0** rather than the single element. Taking that 0 made limsRound round ZERO:
 *  with cutOff 100 / decimals 1 the CCV, LCS and MS rows rendered a red "0.0"
 *  where OG showed 104 / 120 / 115.
 *
 *  A CONSTANT argument arrives as a NumberValueObject and was always fine, which
 *  is why the unit tests and every hand-written probe passed -- the divergence
 *  only appears through the ENGINE on a reference-bearing expression. Measured on
 *  QB26081801_001: arg0 ctor=ArrayValueObject, getValue()=0, on every wrapped
 *  cell. Keep the array branch FIRST; getValue() must never be reached for an
 *  array. */
function unwrap(a: unknown): unknown {
  if (a && typeof a === "object") {
    const o = a as {
      isArray?: () => boolean; getFirstCell?: () => unknown;
      getValue?: () => unknown; value?: unknown; isNull?: () => boolean;
    };
    if (typeof o.isArray === "function" && o.isArray() && typeof o.getFirstCell === "function")
      return unwrap(o.getFirstCell());
    // An EMPTY argument (`_LIMSRound(v,3,10,1,,)` — Apply Rounding writes blank RptLimit / DefaultResult
    // that way) arrives as a NullValueObject whose getValue() is 0. Reading that 0 made RptLimit = 0 and
    // DefaultResult = 0, so every NEGATIVE value (below "limit" 0) became the default 0 — TOC MS1 Rec %
    // −138 showed 0.0 after Apply Rounding (user 2026-09-24). Empty stays empty.
    if (typeof o.isNull === "function" && o.isNull()) return null;
    if (typeof o.getValue === "function") return o.getValue();
    if ("value" in o) return o.value;
  }
  return a;
}

/** `_LIMSRound` as an engine-level function. Extending `BaseFunction` and
 *  registering with `IFunctionService` is the LOW-LEVEL path, deliberately chosen
 *  over the facade's `univerAPI.getFormula().registerFunction`: that facade needs
 *  `sheets-formula.register-function-service`, which the presets build does NOT
 *  put in the redi container — it fails with
 *  `[redi]: Expect 1 dependency item(s) for id "sheets-formula.register-function-service" but get 0`.
 *  `IFunctionService` is part of the formula ENGINE and is always present, since
 *  it is what evaluates every built-in function. */
class LimsRoundFunction extends BaseFunction {
  override calculate(...args: BaseValueObject[]) {
    const [v, sig, cut, dec, rpt, def] = args.map(a => unwrap(a));
    const out = limsRound(v, sig, cut, dec, rpt, def);
    return typeof out === 'number'
      ? NumberValueObject.create(out)
      : StringValueObject.create(String(out));
  }
}

/** Minimal surface of the sheet model the ISBLANK override reads. */
type RawCellReader = {
  getUnit: (id: string) => {
    getSheetBySheetId: (id: string) => {
      getCellRaw: (r: number, c: number) => { v?: unknown; f?: string; si?: string } | null | undefined;
    } | null;
  } | null;
};

/**
 * Excel-faithful ISBLANK, replacing Univer's built-in for the SDMS sheet.
 *
 * WHY OVERRIDE A BUILT-IN. Univer 0.25.1's ISBLANK returns TRUE for a cell whose
 * FORMULA evaluates to "" — Excel returns FALSE (a formula cell is never blank).
 * The Conductivity templates gate Rec %/RPD %/QCStatus on exactly that pattern:
 * `IF(OR(ISBLANK(B7),…,ISBLANK(AC7),…),"",…)` where AC7 is a helper formula that
 * yields "" on non-Dup/MS rows — so in Univer the gate always tripped and the
 * three columns rendered blank on every row (proven headlessly: probe isolated
 * ISBLANK as the ONLY divergent primitive; ISNUMBER(SEARCH), AVERAGEIFS(G:G) and
 * text-number arithmetic all match Excel).
 *
 * The value objects can NOT distinguish the two cases (a reference to a
 * formula-"" cell materialises as null, same as truly empty — measured), so this
 * sets `needsReferenceObject` and reads the RAW cell data instead:
 *   blank ⇔ no formula (`f`/`si`) AND no value (undefined / null).
 * An EMPTY-STRING constant is NOT blank — DevExpress semantics: OG's merge writes "" into
 * every blank FIELD cell and its ISBLANK returns FALSE there (TOC's QCStatus computes past
 * `OR(ISBLANK(R10), ISBLANK(N10), …)` in OG's own blob). Until 2026-09-24 the ''-constant
 * counted as blank, on the belief that OG's cells were truly empty; the parity harness
 * proved otherwise. The CF gate needs the other notion (an empty cell compares as 0) —
 * that is `_LIMSEMPTY` below.
 */
class ExcelIsblank extends BaseFunction {
  constructor(name: unknown, private readonly _instances: RawCellReader) { super(name as never); }
  override needsReferenceObject = true;
  override calculate(ref: BaseValueObject) {
    const r = ref as unknown as {
      isReferenceObject?: () => boolean;
      getRangeData?: () => { startRow: number; startColumn: number };
      getUnitId?: () => string; getSheetId?: () => string;
      isNull?: () => boolean;
    };
    if (!r.isReferenceObject?.()) {
      // Literal argument (e.g. ISBLANK("") / ISBLANK(1)) — never blank in Excel
      // except a genuine null.
      return BooleanValueObject.create(r.isNull?.() ?? false);
    }
    const range = r.getRangeData!();
    const raw = this._instances.getUnit(r.getUnitId?.() ?? '')
      ?.getSheetBySheetId(r.getSheetId?.() ?? '')
      ?.getCellRaw(range.startRow, range.startColumn);
    const hasFormula = !!(raw && ((raw.f && raw.f !== '') || raw.si));
    const blank = !hasFormula && (raw?.v === undefined || raw?.v === null);
    return BooleanValueObject.create(blank);
  }
}

/**
 * `_LIMSEMPTY(ref)` — "holds no value": no formula AND the value is undefined / null / "".
 * The conditional-format gate's notion of empty (sdmsConditionalFormat.ts): Excel and DevExpress
 * compare an empty cell as 0, so `lessThan 60` paints an EMPTY Rec % / RPD % cell pink (client
 * 2026-08-21) — and in OG those cells are ""-constants (the merge writes "" for a blank field),
 * which ISBLANK must NOT report as blank (see ExcelIsblank). A formula-"" cell is text and stays
 * out, as before.
 */
class LimsEmptyFunction extends BaseFunction {
  constructor(name: unknown, private readonly _instances: RawCellReader) { super(name as never); }
  override needsReferenceObject = true;
  override calculate(ref: BaseValueObject) {
    const r = ref as unknown as {
      isReferenceObject?: () => boolean;
      getRangeData?: () => { startRow: number; startColumn: number };
      getUnitId?: () => string; getSheetId?: () => string;
      isNull?: () => boolean;
    };
    if (!r.isReferenceObject?.()) return BooleanValueObject.create(r.isNull?.() ?? false);
    const range = r.getRangeData!();
    const raw = this._instances.getUnit(r.getUnitId?.() ?? '')
      ?.getSheetBySheetId(r.getSheetId?.() ?? '')
      ?.getCellRaw(range.startRow, range.startColumn);
    const hasFormula = !!(raw && ((raw.f && raw.f !== '') || raw.si));
    return BooleanValueObject.create(!hasFormula && (raw?.v === undefined || raw?.v === null || raw?.v === ''));
  }
}

/**
 * Excel-faithful VALUE, replacing Univer's built-in for the SDMS sheet.
 *
 * WHY OVERRIDE A BUILT-IN. Univer 0.25.1's `Value._handleSingleObject` finishes
 * with `NumberValueObject.create(+_text.getValue())`. A reference to a cell whose
 * FORMULA evaluates to "" (e.g. Rec %/RPD % on a fresh, result-less sheet) is
 * resolved to a NullValueObject, and `+null === 0` — so `VALUE("")` returns **0**
 * where Excel raises **#VALUE!**. That single divergence made QCStatus compute on
 * a fresh load: its Cal/CCV branch is
 * `IF(OR(VALUE(N7)>VALUE(Z7), VALUE(N7)<VALUE(Y7)),"Fail","Pass")` — with
 * VALUE(N7)=0 it read 0<90 → "Fail" on every Cal/CCV row (and 0<20 false → "Pass"
 * on Dup), where OG leaves the whole column blank until results are entered. In
 * Excel VALUE(N7)=#VALUE! makes the outer IFERROR blank the cell. (fix 2026-08-13,
 * user OG side-by-side; proven atomically — see qcValAtom probe in the change log.)
 *
 * The body mirrors Univer's built-in EXACTLY; the ONLY change is intercepting a
 * NULL argument (the formula-"" / "" case) and returning #VALUE!. A truly-blank
 * cell also surfaces as null, so `VALUE(blank-cell)` returns #VALUE! here where
 * Excel returns 0 — a deliberate, safe deviation: every VALUE() call in these
 * templates is guarded by an ISBLANK/=""/OR(...="") check before it reaches a
 * possibly-blank cell (O7 guards Y7/Z7; the CCB branch guards G7/X7; L7 guards X7;
 * the Dup branch's VALUE(P7)/VALUE(AB7) hit a formula and a merged limit), so
 * VALUE is never legitimately asked to turn a blank cell into 0. Numbers and
 * numeric strings pass through unchanged, so QCStatus still computes Pass/Fail the
 * moment a real Rec %/RPD % exists.
 */
/** A plain decimal number as text: optional minus, digits, optional fraction — no spaces, sign +, exponent or %. */
const PLAIN_DECIMAL_RE = /^-?(\d+(\.\d+)?|\.\d+)$/;

class ExcelValue extends BaseFunction {
  override calculate(text: BaseValueObject): BaseValueObject {
    const a = text as unknown as {
      isArray?: () => boolean;
      mapValue?: (f: (o: BaseValueObject) => BaseValueObject) =>
        { getRowCount(): number; getColumnCount(): number; get(r: number, c: number): BaseValueObject };
    };
    if (a.isArray?.()) {
      const res = a.mapValue!(o => this._one(o));
      return res.getRowCount() === 1 && res.getColumnCount() === 1
        ? res.get(0, 0)
        : (res as unknown as BaseValueObject);
    }
    return this._one(text);
  }

  private _one(text: BaseValueObject): BaseValueObject {
    const t = text as unknown as {
      isError?: () => boolean; isBoolean?: () => boolean; isNull?: () => boolean;
      isString?: () => boolean; getValue?: () => unknown;
      convertToNumberObjectValue?: () => BaseValueObject & { isError?: () => boolean };
    };
    if (t.isError?.()) return text;
    if (t.isBoolean?.()) return ErrorValueObject.create(ErrorType.VALUE);
    if (t.isNull?.()) return ErrorValueObject.create(ErrorType.VALUE);   // ← the only change vs built-in
    let converted: BaseValueObject = text;
    // VALUE("") / VALUE("  ") is #VALUE! in Excel and DevExpress. Univer's string→number conversion turns
    // an empty string into 0 — VOC 8086's per-run Numeric Result `IFERROR(VALUE(FIELD("NUMERICRESULT")),"")`
    // merges to `IFERROR(VALUE(""),"")` on a row with no result and showed 0 where OG is blank (and fed
    // that 0 to the Summary lookups). (2026-09-24)
    if (t.isString?.() && String(t.getValue?.() ?? '').trim() === '') return ErrorValueObject.create(ErrorType.VALUE);
    if (t.isString?.()) {
      // A plain decimal ("9996.864031", "-0.5") skips Univer's conversion, which tries a date, then a time, then
      // a number on every call (~0.15 s of a VOC / PFAS first calculation). Same number: checked against
      // Univer's own conversion on 330,316 strings, 21,700 of them from saved SDMS sheets — 0 differences
      // (2026-10-01). Anything else (spaces, %, dates, exponents) still goes the full way.
      const raw = String(t.getValue?.() ?? '');
      if (PLAIN_DECIMAL_RE.test(raw)) return NumberValueObject.create(Number(raw));
      const conv = t.convertToNumberObjectValue?.();
      if (conv?.isError?.()) return conv as BaseValueObject;
      if (conv) converted = conv as BaseValueObject;
    }
    return NumberValueObject.create(
      +((converted as unknown as { getValue?: () => unknown }).getValue?.() as number));
  }
}

/** The built-in executor ExcelText hands every ordinary case to (Univer's own TEXT). */
type TextExecutor = BaseFunction & { calculate: (text: BaseValueObject, format: BaseValueObject) => BaseValueObject };

/**
 * Excel-faithful TEXT, replacing Univer's built-in for the SDMS sheet.
 *
 * WHY OVERRIDE A BUILT-IN. `TEXT(G50, 0)` where G50 is a FORMULA that returned "" is "" in Excel and DevExpress
 * (TEXT of a text value returns the text), but Univer resolves the reference to a NullValueObject and its TEXT
 * turns null into 0 → "0". PFAS 533's internal-standard rows chain exactly that: ISTD Response
 * `IFERROR(VALUE(BA50),"")` is "" with no response, Numeric Result `TEXT(G50,0)` then read "0" and Result
 * `VALUE(J50)` 0, so Apply Rounding wrapped the cell and the first save stored Result '0.0' / '0' on 21 rows
 * where OG stores NULL (comparison 2026-09-29).
 *
 * Like ExcelIsblank it reads the RAW cell to tell the cases apart: a formula cell whose value is null returned "",
 * so it is formatted as the empty text. A truly empty cell keeps Excel's TEXT(blank) = TEXT(0), and every other
 * argument goes to the built-in unchanged.
 */
class ExcelText extends BaseFunction {
  constructor(name: unknown, private readonly _instances: RawCellReader, readonly builtin: TextExecutor) { super(name as never); }
  override minParams = 2;
  override maxParams = 2;
  override needsReferenceObject = true;
  override isArgumentsIgnoreNumberPattern() { return true; }
  override calculate(text: BaseValueObject, format: BaseValueObject): BaseValueObject {
    type Ref = BaseValueObject & {
      isReferenceObject: () => boolean; toArrayValueObject: () => BaseValueObject;
      getRangeData: () => { startRow: number; endRow: number; startColumn: number; endColumn: number };
      getUnitId: () => string; getSheetId: () => string;
    };
    const valueOf = (o: BaseValueObject): BaseValueObject => {
      const r = o as Ref;
      if (!r.isReferenceObject?.()) return o;
      const arr = r.toArrayValueObject() as BaseValueObject & { getRowCount?: () => number; getColumnCount?: () => number; get?: (row: number, col: number) => BaseValueObject };
      return arr.getRowCount?.() === 1 && arr.getColumnCount?.() === 1 ? (arr.get!(0, 0) ?? arr) : arr;
    };
    let value = valueOf(text);
    const ref = text as Ref;
    if (ref.isReferenceObject?.() && (value as { isNull?: () => boolean }).isNull?.()) {
      const range = ref.getRangeData();
      if (range.startRow === range.endRow && range.startColumn === range.endColumn) {
        const raw = this._instances.getUnit(ref.getUnitId())?.getSheetBySheetId(ref.getSheetId())
          ?.getCellRaw(range.startRow, range.startColumn);
        if (raw && ((raw.f && raw.f !== '') || raw.si)) value = StringValueObject.create('');
      }
    }
    return this.builtin.calculate(value, valueOf(format)) as BaseValueObject;
  }
}

/**
 * Excel-faithful ROUND, replacing Univer's built-in for the SDMS sheet.
 *
 * WHY OVERRIDE A BUILT-IN. Univer 0.25.1's ROUND coerces a reference to a cell
 * whose FORMULA evaluates to "" (a NullValueObject — the same collapse behind
 * the VALUE("")→0 bug above) to the number 0, where Excel raises #VALUE!. The
 * pH template's QCStatus is
 *   `IFERROR(IF(OR(ISBLANK(J10),…)," ", … IF(AND(ROUND(J10,1)<=0.1,
 *    ROUND(J10,1)>=-0.1),"Pass","Fail") …), "")`
 * — on a fresh result-less sheet J10 is a formula returning "", so Excel/OG
 * blank the cell through the IFERROR, while Univer's ROUND(0-coerced) computed
 * "Pass" into every CCV/LCS/Dup row (user pH side-by-side 2026-08-17; the
 * Excel-ISBLANK override above is what correctly lets evaluation REACH the
 * ROUND — leaving the built-in ROUND as the last divergent primitive).
 *
 * Same deliberate deviation as ExcelValue: a truly-blank cell also surfaces as
 * null and returns #VALUE! here (Excel would give 0) — acceptable because every
 * template ROUND sits behind an IFERROR/blank guard, so a blank input must
 * yield the guarded blank, never a rounded 0. Numbers and numeric strings
 * round exactly as Excel: half away from zero; negative digits round left of
 * the decimal point; blank digits mean 0.
 */
class ExcelRound extends BaseFunction {
  override minParams = 2;
  override maxParams = 2;
  override calculate(value: BaseValueObject, digits: BaseValueObject): BaseValueObject {
    const d = this._digits(digits);
    if (typeof d !== 'number') return d;
    const a = value as unknown as {
      isArray?: () => boolean;
      mapValue?: (f: (o: BaseValueObject) => BaseValueObject) =>
        { getRowCount(): number; getColumnCount(): number; get(r: number, c: number): BaseValueObject };
    };
    if (a.isArray?.()) {
      const res = a.mapValue!(o => this._one(o, d));
      return res.getRowCount() === 1 && res.getColumnCount() === 1
        ? res.get(0, 0)
        : (res as unknown as BaseValueObject);
    }
    return this._one(value, d);
  }

  /** Digits argument: blank → 0 (Excel), boolean → 1/0, numeric text coerces,
   *  an error propagates, anything else → #VALUE!. Truncated toward zero. */
  private _digits(digits: BaseValueObject): number | BaseValueObject {
    let o = digits as unknown as {
      isError?: () => boolean; isNull?: () => boolean; isBoolean?: () => boolean;
      isArray?: () => boolean; getFirstCell?: () => BaseValueObject; getValue?: () => unknown;
    };
    if (o.isArray?.()) o = (o.getFirstCell?.() ?? digits) as typeof o;
    if (o.isError?.()) return o as unknown as BaseValueObject;
    if (o.isNull?.()) return 0;
    if (o.isBoolean?.()) return o.getValue?.() ? 1 : 0;
    const n = Number(o.getValue?.());
    return Number.isFinite(n) ? Math.trunc(n) : ErrorValueObject.create(ErrorType.VALUE);
  }

  private _one(value: BaseValueObject, d: number): BaseValueObject {
    const t = value as unknown as {
      isError?: () => boolean; isBoolean?: () => boolean; isNull?: () => boolean;
      getValue?: () => unknown;
    };
    if (t.isError?.()) return value;
    if (t.isNull?.()) return ErrorValueObject.create(ErrorType.VALUE);   // formula-"" / blank — see note
    let n: number;
    if (t.isBoolean?.()) n = t.getValue?.() ? 1 : 0;
    else {
      const raw = t.getValue?.();
      if (typeof raw === 'number') n = raw;
      else {
        const s = String(raw ?? '').trim();
        const parsed = Number(s);
        if (s === '' || !Number.isFinite(parsed)) return ErrorValueObject.create(ErrorType.VALUE);
        n = parsed;
      }
    }
    const dd = Math.max(-15, Math.min(15, d));
    const f = 10 ** dd;
    const r = Math.sign(n) * Math.round(Math.abs(n) * f + Number.EPSILON) / f;
    return NumberValueObject.create(r);
  }
}

/**
 * `SGFNT(value, sigFigs)` — OG's OTHER custom worksheet function, found by the
 * all-templates audit (2026-08-17): 4 active templates call it and every cell
 * rendered #NAME? in AI. OG registers it as DevExpress ICustomFunction
 * `SignificantNotation` (BTLIMS.Module/BusinessObjects/FuncSignificantNotation.cs)
 * → `LIMSFormula.FormatSF(value, sf, true)` (DynamicReportDesigner .../ML/
 * LIMSFormula.vb) — "format to N significant figures".
 *
 * DELIBERATE DEVIATION: FormatSF is ~200 lines of VB string surgery with
 * layered bug-workarounds (even-digit-then-exact-5 tie handling per tracker
 * 2334/2925/3302, notation branches). This implementation reuses the SAME
 * lab-approved sig-fig rule the rest of AI already runs (`toSigFig`, the port
 * of RoundingHelper.ApplyRounding used by `_LIMSRound` and the server-side
 * LimsRounding) — half AWAY from zero. Exact `…X5`-tie inputs may differ from
 * OG's VB in the last significant digit; flagged for the module owner rather
 * than porting the VB quirks verbatim. Zero → "0" (FormatSF's own first case);
 * non-numeric/blank input → #VALUE! (Convert.ToDouble throws in OG).
 */
class SgfntFunction extends BaseFunction {
  override minParams = 2;
  override maxParams = 2;
  override calculate(value: BaseValueObject, sig: BaseValueObject): BaseValueObject {
    // A cell reference can arrive as a 1×1 array object — unwrap it so the
    // null/error checks see the actual cell value (same as ExcelRound._digits).
    const unwrap = (o: BaseValueObject): BaseValueObject => {
      const a = o as unknown as { isArray?: () => boolean; getFirstCell?: () => BaseValueObject };
      return a.isArray?.() ? (a.getFirstCell?.() ?? o) : o;
    };
    value = unwrap(value); sig = unwrap(sig);
    const t = value as unknown as { isError?: () => boolean; isNull?: () => boolean; getValue?: () => unknown };
    const s = sig as unknown as { isError?: () => boolean; getValue?: () => unknown };
    if (t.isError?.()) return value;
    if (s.isError?.()) return sig;
    // Blank / formula-"" input: NullValueObject.getValue() is 0, which would
    // silently format as "0" — OG's Convert.ToDouble("") throws instead.
    if (t.isNull?.()) return ErrorValueObject.create(ErrorType.VALUE);
    const v = valueNum(t.getValue?.());
    const n = int(s.getValue?.());
    if (v === null || n === null || n <= 0) return ErrorValueObject.create(ErrorType.VALUE);
    if (v === 0) return StringValueObject.create('0');
    // SGFNT = OG FormatSF(value, sf, /*notation*/ true), ported VERBATIM in sgfntFormat.ts
    // (byte-identical to the compiled ReportMultilanguage.LIMSFormula DLL — 236-case golden test).
    return StringValueObject.create(formatSF(v, n));
  }
}

/** OG SGFNT display string — delegates to the VERBATIM `FormatSF` port (sgfntFormat.ts),
 *  which is byte-identical to the compiled OG DLL. Kept as a stable export for callers/tests. */
export function formatSigNotation(v: number, sig: number): string {
  return formatSF(v, sig);
}

/**
 * Excel-faithful ordering comparison for a blank/formula-"" operand.
 *
 * WHY PATCH A PROTOTYPE. Comparison operators (`<` `>` `<=` `>=`) are not
 * registerable functions — the engine evaluates them via each value object's
 * `compare`. Univer 0.25.1's `NullValueObject.compare` delegates a NUMBER
 * comparison to `NumberValueObject.create(0)`, i.e. it compares a blank AS 0.
 * A cell whose FORMULA returns "" resolves to a NullValueObject (the same
 * collapse behind the VALUE("")→0 bug), so `G7 < RptLimit` on a result-less
 * sheet became `0 < 20` = TRUE and PRE-FILLED the SDMS "Result" column with the
 * below-limit marker "<20" where OG is blank (Alkalinity template 8085 H7:
 * `IF(G7<VALUE(K7),"<"&K7,G7)`, guarded only by `ISBLANK(G7)` which is correctly
 * FALSE for a formula cell). Excel treats a formula-"" as TEXT, and text always
 * sorts AFTER any number, so `"" < 20` is FALSE — which is why OG leaves it blank.
 *
 * The fix delegates a null-vs-NUMBER ORDERING comparison to the empty-string
 * object (its `compare` already implements Excel's text>number rule — a literal
 * `""<20` is correctly FALSE in Univer). Equality (`=` / `<>`) is left untouched
 * so guarded `blank = 0` checks (e.g. Rec %'s `OR(G7=0,K7=0)`) keep Univer's 0
 * semantics. Real number<number comparisons are unaffected (they never reach
 * NullValueObject.compare). Applied once, globally — no per-template code.
 * (fix 2026-08-13, user OG side-by-side; Alkalinity Result pre-fill)
 */
let _nullComparePatched = false;
function patchNullOrderingCompare(): void {
  if (_nullComparePatched) return;
  const proto = NullValueObject.prototype as unknown as {
    compare: (v: unknown, op: string, cs?: boolean) => unknown;
  };
  const original = proto.compare;
  proto.compare = function (this: unknown, valueObject: unknown, operator: string, cs?: boolean) {
    const v = valueObject as { isNumber?: () => boolean } | undefined;
    if ((operator === '<' || operator === '>' || operator === '<=' || operator === '>=')
        && v?.isNumber?.()) {
      return (StringValueObject.create('') as unknown as {
        compare: (v: unknown, op: string, cs?: boolean) => unknown;
      }).compare(valueObject, operator, cs);
    }
    return original.call(this, valueObject, operator, cs);
  };
  // ARITHMETIC gets the same treatment (2026-08-17, user PFAS side-by-side).
  // Univer's NullValueObject arithmetic coerces to 0, so the PFAS template's
  //   Dev % `(J8 - M8) / M8 * 100`  (J8 = Numeric Result, a formula-"" until
  //   results exist) computed (0-spike)/spike*100 = -100.0, and its Rec %
  //   divisions computed 0.0, pre-filling every CCCL row where OG is blank —
  //   in Excel `"" - number` raises #VALUE! and the template's IFERROR blanks
  //   the cell. Delegating null-vs-NUMBER arithmetic to the empty-STRING object
  //   reproduces Excel's text-operand error; null-vs-anything-else keeps
  //   Univer's behaviour. Same formula-""-collapses-to-null rationale as the
  //   ordering patch above; templates were authored against DevExpress/Excel,
  //   which ALWAYS errors here — OG never renders a number from this shape.
  // The guard fires for a NUMBER **or STRING** operand: merged FIELD cells hold
  // TEXT (SpikeAmount = "37600"), so `formula-"" - "37600"` must error exactly
  // like `formula-"" - 37600` — the PFAS Dev% still computed -100.0 with a
  // number-only guard (headless repro, 2026-08-17). Excel errors for both.
  for (const op of ['plus', 'minus', 'multiply', 'divided'] as const) {
    const orig = (proto as unknown as Record<string, (v: unknown) => unknown>)[op]!;
    (proto as unknown as Record<string, (v: unknown) => unknown>)[op] = function (this: unknown, other: unknown) {
      const o = other as { isNumber?: () => boolean; isString?: () => boolean } | undefined;
      if (o?.isNumber?.() || o?.isString?.()) {
        return (StringValueObject.create('') as unknown as Record<string, (v: unknown) => unknown>)[op]!(other);
      }
      return orig.call(this, other);
    };
  }
  _nullComparePatched = true;
}

/**
 * Register `_LIMSRound` + the Excel-faithful ISBLANK with Univer's engine.
 *
 * Pass the `univer` INSTANCE (not the facade API) — the injector hangs off it.
 *
 * ⚠️ CALL IMMEDIATELY **AFTER** `createWorkbook`, not before. Proven by headless
 * probes: executors registered BEFORE the first unit exists are wiped when the
 * built-ins land during first-unit boot (registration looked successful, then the
 * engine ran without it — the exact "fix changed nothing" failure seen live);
 * registered synchronously AFTER createUnit they win, and the first ASYNC
 * calculation pass already uses them (Rec % computed 0.0708 in the probe).
 *
 * Best-effort: a failure must not take the sheet down, but it IS logged loudly —
 * a regression here blanks every `_LIMSRound`/ISBLANK-gated cell and reads like
 * a data bug rather than a missing function.
 */
export function registerLimsRound(univer: unknown): void {
  try {
    // Excel-faithful `<`/`>` for a formula-"" (null) vs a number — see the
    // patchNullOrderingCompare note. Idempotent; safe to call every mount.
    patchNullOrderingCompare();
    const injector = (univer as { __getInjector?: () => { get: (id: unknown) => unknown } })
      ?.__getInjector?.();
    if (!injector) {
      // eslint-disable-next-line no-console
      console.error('[SDMS] Univer injector unavailable — `_LIMSRound`/ISBLANK/VALUE NOT registered; '
        + 'Rec %/RPD %/QCStatus and every rounded cell will render blank.');
      return;
    }
    const fnService = injector.get(IFunctionService) as {
      registerExecutors?: (...f: BaseFunction[]) => void;
    };
    if (!fnService?.registerExecutors) {
      // eslint-disable-next-line no-console
      console.error('[SDMS] IFunctionService unavailable — `_LIMSRound`/ISBLANK/VALUE NOT registered.');
      return;
    }
    const instances = injector.get(IUniverInstanceService) as RawCellReader;
    // TEXT wraps the engine's own executor; on a re-register take the built-in out of the earlier wrapper.
    const current = (fnService as { getExecutor?: (t: unknown) => BaseFunction | null | undefined }).getExecutor?.('TEXT');
    const builtinText = (current instanceof ExcelText ? current.builtin : current) as TextExecutor | null | undefined;
    // Executor tokens are looked up CASE-NORMALISED (uppercase): registering the
    // mixed-case '_LIMSRound' leaves the formula resolving to #NAME? (measured —
    // the sheets-formula facade does `getFunctionName(...).toUpperCase()` for the
    // same reason). Register UPPERCASE; the mixed-case template text still matches.
    // (The FUNCTION_NAMES_MATH cast only satisfies the token type — `_LIMSROUND`
    // is a custom name; ISBLANK deliberately shadows the built-in.)
    fnService.registerExecutors(
      new LimsRoundFunction('_LIMSROUND' as unknown as typeof FUNCTION_NAMES_MATH.ABS),
      new ExcelIsblank('ISBLANK', instances),
      new LimsEmptyFunction('_LIMSEMPTY' as unknown as typeof FUNCTION_NAMES_MATH.ABS, instances),
      new ExcelValue('VALUE' as unknown as typeof FUNCTION_NAMES_MATH.ABS),
      new ExcelRound('ROUND' as unknown as typeof FUNCTION_NAMES_MATH.ABS),
      new SgfntFunction('SGFNT' as unknown as typeof FUNCTION_NAMES_MATH.ABS),
    );
    if (builtinText) fnService.registerExecutors(new ExcelText('TEXT', instances, builtinText));
    // eslint-disable-next-line no-console
    else console.error('[SDMS] built-in TEXT executor unavailable — the Excel-faithful TEXT is NOT registered.');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[SDMS] _LIMSRound/ISBLANK/VALUE registration failed; dependent formulas will not compute.', e);
  }
}
